// src/components/ImageUpload.tsx
import { useState, useRef, useEffect } from 'react';
import { Upload, X, Loader, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ImageUploadProps {
  onImageUploaded?: (url: string) => void; // Made optional
  currentImage?: string;
  onRemove?: () => void;
  className?: string;
}

// Image compression utility
const compressImage = async (file: File, maxWidth = 1200, quality = 0.7): Promise<File> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      let width = img.width;
      let height = img.height;
      
      if (width > maxWidth) {
        height = (maxWidth / width) * height;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx?.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            resolve(file);
          }
        },
        'image/jpeg',
        quality
      );
    };
    
    img.src = URL.createObjectURL(file);
  });
};

// Get optimized image URL
const getOptimizedImageUrl = (url: string, width = 800, quality = 80) => {
  if (!url) return '';
  
  // Check if it's a Supabase URL
  if (url.includes('supabase.co')) {
    // Add transformation parameters
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}&quality=${quality}&format=webp`;
  }
  
  return url;
};

// Progressive Image Component
const ProgressiveImage = ({ src, alt, className = '' }: { src: string; alt: string; className?: string }) => {
  const [imgSrc, setImgSrc] = useState('/placeholder.jpg');
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = src;
    img.onload = () => {
      setImgSrc(src);
      setImgLoaded(true);
    };
  }, [src]);

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={`${className} transition-all duration-500 ${imgLoaded ? 'blur-0' : 'blur-sm'}`}
      loading="lazy"
    />
  );
};

// Custom hook for image loading
const useImageLoader = (src: string) => {
  const [imageState, setImageState] = useState({
    loading: true,
    error: false,
    src: ''
  });

  useEffect(() => {
    setImageState({ loading: true, error: false, src: '' });
    
    const img = new Image();
    img.src = src;
    
    img.onload = () => {
      setImageState({ loading: false, error: false, src });
    };
    
    img.onerror = () => {
      setImageState({ loading: false, error: true, src: '' });
    };
    
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  return imageState;
};

// Lazy Image Component with Intersection Observer
const LazyImage = ({ src, alt, className = '', placeholder = '/placeholder.jpg' }: { src: string; alt: string; className?: string; placeholder?: string }) => {
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className={className}>
      {inView && (
        <img
          src={getOptimizedImageUrl(src)}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setLoaded(true)}
          loading="lazy"
        />
      )}
      {!loaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}
    </div>
  );
};

export function ImageUpload({ onImageUploaded, currentImage, onRemove, className = '' }: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(currentImage || null);

  const uploadImage = async (file: File) => {
    try {
      setUploading(true);
      setError(null);
      
      // Compress the image before upload
      const compressedFile = await compressImage(file, 1200, 0.7);
      
      const fileExt = compressedFile.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, compressedFile, {
          cacheControl: '31536000', // Cache for 1 year
          upsert: false,
          contentType: 'image/webp' // Use WebP format
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      setPreview(publicUrl);
      
      // Check if onImageUploaded is a function before calling it
      if (typeof onImageUploaded === 'function') {
        onImageUploaded(publicUrl);
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('L\'image ne doit pas dépasser 10 MB');
      return;
    }

    await uploadImage(file);
  };

  const handleRemove = () => {
    setPreview(null);
    setError(null);
    if (onRemove) onRemove();
  };

  return (
    <div className={className}>
      {!preview ? (
        <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#D4452C] hover:bg-gray-50 transition-colors">
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            {uploading ? (
              <>
                <Loader className="h-12 w-12 text-[#D4452C] animate-spin mb-4" />
                <p className="text-sm text-gray-600">Téléchargement et compression en cours...</p>
              </>
            ) : (
              <>
                <Upload className="h-12 w-12 text-gray-400 mb-4" />
                <p className="mb-2 text-sm text-gray-600">
                  <span className="font-semibold">Cliquez pour télécharger</span> ou glissez-déposez
                </p>
                <p className="text-xs text-gray-500">PNG, JPG, WEBP, GIF (MAX. 10MB)</p>
                <p className="text-xs text-green-600 mt-2">Les images seront automatiquement compressées</p>
              </>
            )}
          </div>
          <input
            type="file"
            className="hidden"
            accept="image/*"
            onChange={handleFileChange}
            disabled={uploading}
          />
        </label>
      ) : (
        <div className="relative w-full h-64 border-2 border-gray-200 rounded-lg overflow-hidden group">
          <LazyImage
            src={preview}
            alt="Preview"
            className="w-full h-full"
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
            <button
              type="button"
              onClick={handleRemove}
              className="opacity-0 group-hover:opacity-100 transition-opacity bg-red-600 text-white p-3 rounded-full hover:bg-red-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
          <ImageIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
    </div>
  );
}

interface MultiImageUploadProps {
  onImagesUploaded: (urls: string[]) => void;
  currentImages?: string[];
  maxImages?: number;
  className?: string;
}

export function MultiImageUpload({
  onImagesUploaded,
  currentImages = [],
  maxImages = 10,
  className = ''
}: MultiImageUploadProps) {
  const [images, setImages] = useState<string[]>(currentImages);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // FIX: Sync state with props if props change (e.g. from localStorage load)
  useEffect(() => {
    // Only update if they are different to avoid infinite loops
    if (JSON.stringify(currentImages) !== JSON.stringify(images)) {
        setImages(currentImages);
    }
  }, [currentImages]);

  const uploadImage = async (file: File) => {
    try {
      // Compress the image before upload
      const compressedFile = await compressImage(file, 1200, 0.7);
      
      const fileExt = compressedFile.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, compressedFile, {
          cacheControl: '31536000',
          upsert: false,
          contentType: 'image/webp'
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (err) {
      console.error('Error uploading image:', err);
      throw err;
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (images.length + files.length > maxImages) {
      setError(`Vous ne pouvez télécharger que ${maxImages} images maximum`);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const uploadPromises = files.map(file => {
        if (!file.type.startsWith('image/')) {
          throw new Error('Veuillez sélectionner uniquement des images');
        }
        if (file.size > 10 * 1024 * 1024) {
          throw new Error('Chaque image ne doit pas dépasser 10 MB');
        }
        return uploadImage(file);
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      const newImages = [...images, ...uploadedUrls];
      setImages(newImages);
      onImagesUploaded(newImages);
    } catch (err) {
      console.error('Error uploading images:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    onImagesUploaded(newImages);
  };

  return (
    <div className={className}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        {images.map((image, index) => (
          <div key={index} className="relative aspect-video border-2 border-gray-200 rounded-lg overflow-hidden group">
            <LazyImage
              src={image}
              alt={`Image ${index + 1}`}
              className="w-full h-full"
            />
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}

        {images.length < maxImages && (
          <label className="aspect-video border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#D4452C] hover:bg-gray-50 transition-colors flex flex-col items-center justify-center">
            {uploading ? (
              <Loader className="h-8 w-8 text-[#D4452C] animate-spin" />
            ) : (
              <>
                <Upload className="h-8 w-8 text-gray-400 mb-2" />
                <p className="text-xs text-gray-600 text-center px-2">Ajouter des images</p>
                <p className="text-xs text-green-600 mt-1">Compression automatique</p>
              </>
            )}
            <input
              type="file"
              className="hidden"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              disabled={uploading}
            />
          </label>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
          <ImageIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <p className="text-sm text-gray-500 mt-2">
        {images.length} / {maxImages} images téléchargées
      </p>
    </div>
  );
}